import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { DataField, FormatType } from '../../types/viewspec';

interface DistributionChartProps {
  title?: string;
  data: Record<string, unknown>[];
  xAxis: DataField;
  yAxis: DataField | DataField[];
  chartType: 'bar' | 'pie' | 'horizontalBar';
  showLegend?: boolean;
  onSegmentClick?: (payload: { field: string; value: string; label?: string }) => void;
}

// Pentatonic brand colors for charts
const COLORS = ['#171717', '#00FBA9', '#3941EB', '#FF4C00', '#5BC48B', '#9EA8FF', '#FFB788', '#A9A9A9'];

function formatValue(value: unknown, format?: FormatType): string {
  if (value === null || value === undefined) return '';

  switch (format) {
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : String(value);
    case 'currency':
      return typeof value === 'number'
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
        : String(value);
    case 'percent':
      return typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : String(value);
    default:
      return String(value);
  }
}

export default function DistributionChart({
  title,
  data,
  xAxis,
  yAxis,
  chartType,
  showLegend = true,
  onSegmentClick,
}: DistributionChartProps) {
  const yAxes = Array.isArray(yAxis) ? yAxis : [yAxis];

  const handleClick = (entry: Record<string, unknown>) => {
    if (onSegmentClick && entry) {
      const value = String(entry[xAxis.key] || '');
      onSegmentClick({
        field: xAxis.key,
        value,
        label: xAxis.label,
      });
    }
  };

  return (
    <div className="bg-grey-light p-4 flex flex-col" style={{ minHeight: '300px' }}>
      {title && (
        <h3 className="text-h3 font-light text-black mb-4">
          {title}
        </h3>
      )}
      <div className="flex-1" style={{ minHeight: '250px' }}>
        <ResponsiveContainer width="100%" height={250}>
          {chartType === 'pie' ? (
            <PieChart>
              <Pie
                data={data}
                dataKey={yAxes[0]?.key || 'value'}
                nameKey={xAxis.key}
                cx="50%"
                cy="50%"
                outerRadius="80%"
                innerRadius="40%"
                paddingAngle={1}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
                onClick={(_, index) => handleClick(data[index] || {})}
                style={{ cursor: onSegmentClick ? 'pointer' : 'default' }}
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #A9A9A9',
                  borderRadius: '0',
                  boxShadow: 'none',
                  fontFamily: 'Atkinson Hyperlegible Next',
                }}
                formatter={(value: number) => [
                  formatValue(value, yAxes[0]?.format),
                  yAxes[0]?.label || 'Value',
                ]}
              />
              {showLegend && <Legend wrapperStyle={{ fontFamily: 'Atkinson Hyperlegible Next', fontSize: 12 }} />}
            </PieChart>
          ) : (
            <BarChart
              data={data}
              layout={chartType === 'horizontalBar' ? 'vertical' : 'horizontal'}
              margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#A9A9A9" strokeOpacity={0.3} />
              {chartType === 'horizontalBar' ? (
                <>
                  <XAxis
                    type="number"
                    tick={{ fontSize: 12, fill: '#A9A9A9', fontFamily: 'Atkinson Hyperlegible Next' }}
                    tickFormatter={(v) => formatValue(v, yAxes[0]?.format)}
                    axisLine={{ stroke: '#A9A9A9' }}
                    tickLine={{ stroke: '#A9A9A9' }}
                  />
                  <YAxis
                    type="category"
                    dataKey={xAxis.key}
                    tick={{ fontSize: 12, fill: '#A9A9A9', fontFamily: 'Atkinson Hyperlegible Next' }}
                    axisLine={{ stroke: '#A9A9A9' }}
                    tickLine={{ stroke: '#A9A9A9' }}
                    width={100}
                  />
                </>
              ) : (
                <>
                  <XAxis
                    dataKey={xAxis.key}
                    tick={{ fontSize: 12, fill: '#A9A9A9', fontFamily: 'Atkinson Hyperlegible Next' }}
                    axisLine={{ stroke: '#A9A9A9' }}
                    tickLine={{ stroke: '#A9A9A9' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#A9A9A9', fontFamily: 'Atkinson Hyperlegible Next' }}
                    tickFormatter={(v) => formatValue(v, yAxes[0]?.format)}
                    axisLine={{ stroke: '#A9A9A9' }}
                    tickLine={{ stroke: '#A9A9A9' }}
                  />
                </>
              )}
              <Tooltip
                contentStyle={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #A9A9A9',
                  borderRadius: '0',
                  boxShadow: 'none',
                  fontFamily: 'Atkinson Hyperlegible Next',
                }}
                formatter={(value: number, name: string) => [
                  formatValue(value, yAxes.find((y) => y.key === name)?.format),
                  yAxes.find((y) => y.key === name)?.label || name,
                ]}
              />
              {showLegend && <Legend wrapperStyle={{ fontFamily: 'Atkinson Hyperlegible Next', fontSize: 12 }} />}
              {yAxes.map((axis, i) => (
                <Bar
                  key={axis.key}
                  dataKey={axis.key}
                  name={axis.label}
                  fill={axis.color || COLORS[i % COLORS.length]}
                  radius={0}
                  onClick={(entry) => handleClick(entry as Record<string, unknown>)}
                  style={{ cursor: onSegmentClick ? 'pointer' : 'default' }}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
